import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grow,
  Zoom,
  Fade,
  MobileStepper,
  Stack,
} from '@mui/material';
import { StoreOutlined, EmailOutlined, Person, RocketLaunchOutlined, KeyboardArrowLeft, KeyboardArrowRight, WavingHandOutlined, ThumbUpOutlined, Check, SupportAgentOutlined, InfoOutlined } from '@mui/icons-material';
import { withRouter } from 'react-router';
import StyledPage from '../../common/StyledPage';
import StyledSubHeader from '../../common/StyledSubHeader';
import StyledSection from '../../common/StyledSection';
import IconTextSection from '../../common/IconTextSection';
import { getUserId, getUserObject } from '../../../selectors/UserSelectors';
import { getAllSites } from '../../../selectors/SiteSelectors';
import { ContainedButton, LoadingButton, TextButton, TooltipIconButton } from '../../common/StyledButtons';
import Colors from '../../../constants/Colors';
import { ICON_TITLE_TEXT_STYLE } from '../../../constants/Styles';
import { setupUser } from '../../../actions/profile/UserActions';
import { NOTIFICATION_TYPE, showNotificationBar } from '../../../actions/error-pages/NotificationBarActions';
import { observer } from 'mobx-react-lite';

const UserAccountSetupPage = ({ history }) => {
  const [isSetup, setIsSetup] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [stepKey, setStepKey] = useState(0);
  const [steps, setSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const user = getUserObject();
  const sites = getAllSites();

  useEffect(() => {
    validateSession();
    buildSession();

    const timeout = setTimeout(() => setShowWelcome(true), 150);
    return () => {
      clearTimeout(timeout);
    }
  }, []);

  const validateSession = () => {
    // check if user is NOT set up
    if (!user || !user.metadata || !user.metadata.dashboard_setup_timestamp) return;

    // check if user is set up but has no sites
    if (!sites || sites.length === 0) {
      setIsSetup(true); // user is set up but has no sites
      return;
    }

    // if user is set up and has sites, then redirect to first site
    redirectToDashIfUserHasSite();
  }

  const redirectToDashIfUserHasSite = () => {
    const firstSite = sites[0];
    if (firstSite && firstSite.site_id) {
      history.replace(`/dashboard/site/${firstSite.site_id}`);
    }
  }

  const buildSession = () => {
    const builtSteps = [renderWelcomeSlide];
    if (sites && sites.length > 0) {
      builtSteps.push(renderBusinessLineupSlide);
      const isPrimary = sites.some(site => site.site_security.is_primary_user )
      if (isPrimary) builtSteps.push(renderPrimaryUserSlide);
      builtSteps.push(renderFinalSlide);
    } else builtSteps.push(renderNoBusinessesSlide);
    setSteps(builtSteps);
  }

  const handleNext = () => {

    if (stepKey >= steps.length-1) {
      handleFinish();
      return;
    }

    setFadeIn(false);
    setTimeout(() => {
      setStepKey((prev) => prev + 1);
      setActiveStep((prev) => prev + 1);
      setFadeIn(true);
    }, 300);
  };

  const handleBack = () => {
    setFadeIn(false);
    setTimeout(() => {
      setStepKey((prev) => prev - 1);
      setActiveStep((prev) => prev - 1);
      setFadeIn(true);
    }, 300);
  };

  const handleFinish = async () => {
    try {
      setIsLoading(true);
      await setupUser(getUserId())

      // redirect if user has sites
      redirectToDashIfUserHasSite();
      setIsSetup(true);
    } catch (err) {
      showNotificationBar(NOTIFICATION_TYPE.ERROR, 'An error occurred while completing setup. Please try again.');
      const awaitedErr = await err;
      console.error(awaitedErr);
    } finally {
      setIsLoading(false);
    }
  }

  const renderWelcome = () => (
    <Box sx={{ textAlign: 'center', mb: 2 }}>
      <Zoom in={showWelcome} timeout={300}>
        <ThumbUpOutlined sx={{ fontSize: 60, color: Colors.primaryColor, mb: 0 }} />
      </Zoom>
      <Grow in={showWelcome} timeout={500} style={{ transitionDelay: showWelcome ? '200ms' : '0ms' }}>
        <Box>
          <h4 style={{ marginBottom: 0, paddingBottom: 0, marginTop: '10px' }}>
            Glad to have you here, {user.user_personal.first_name}.
          </h4>
          <p style={{ paddingTop: 0, marginTop: 0 }}>
            Let's get started.
          </p>
        </Box>
      </Grow>
    </Box>
  );

  const renderNoLinkedBusinessNotice = () => (
    <Box sx={{ textAlign: 'center', mb: 2 }}>
      <Zoom in={showWelcome} timeout={300}>
        <StoreOutlined sx={{ fontSize: 60, color: Colors.primaryColor, mb: 0 }} />
      </Zoom>
      <Grow in={showWelcome} timeout={500} style={{ transitionDelay: showWelcome ? '200ms' : '0ms' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Box sx={{ maxWidth: '600px' }}>
            <h4 style={{ marginBottom: 0, paddingBottom: 0, marginTop: '10px' }}>
              We're waiting on the doors to open, {user.user_personal.first_name}.
            </h4>
            <p style={{ paddingTop: 0, marginTop: 0 }}>
              Looks like your account isn't linked to a business just yet. Once linked, your BCK Business Manager will come to life.
            </p>
          </Box>
        </Box>
      </Grow>
    </Box>
  );
  

  const renderWelcomeSlide = () => (
    <>
      <h4 style={{ fontSize: '24px', textAlign: 'center', margin: 0 }}>
        Here's what you're working with
      </h4>
      <p style={{ textAlign: 'center', marginTop: 8 }}>
        Powerful tools to help you simplify, scale, and succeed online.
      </p>
      <Box display="flex" justifyContent="center" mt={2}>
        <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Check sx={{ color: Colors.primaryColor, mr: 1 }} />
            Access online performance insights
          </li>
          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Check sx={{ color: Colors.primaryColor, mr: 1 }} />
            Manage website updates easily
          </li>
          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Check sx={{ color: Colors.primaryColor, mr: 1 }} />
            Track project process & change requests
          </li>
        </Box>
      </Box>
    </>
  );

  const renderBusinessLineupSlide = () => (
    <>
      <h4 style={{ fontSize: '24px', textAlign: 'center', margin: 0 }}>
        Your Business Lineup
      </h4>
      <p style={{ textAlign: 'center', marginTop: 8 }}>
        Here’s the business you’re set to manage.
      </p>
      <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px', borderRadius: '8px', }}>
        <List style={{ flex: 1 }}>
          {sites && sites.map((site, index) => (
            <ListItem
              dense
              key={site.site_id}
              sx={{
                backgroundColor: '#fff',
                marginBottom: '5px',
                borderBottom: index !== sites.length - 1 ? '1px solid #e0e0e0' : 'none',
              }}
            >
              <ListItemAvatar>
                {site && site.site_details && site.site_details.site_img_url ?
                  <Avatar alt={site.site_details.site_name} src={site.site_details.site_img_url} />
                  :
                  <Avatar><StoreOutlined /></Avatar>
                }
              </ListItemAvatar>
              <ListItemText
                primary={site.site_details.site_name}
                secondary={site.site_security.is_primary_user ? 'Primary User Access' : 'Standard User'}
              />
            </ListItem>
          ))}
          {(!sites || sites.length === 0) &&
              <p>It doesn't look like you have access to any accounts at this time.</p>
          }
        </List>
      </Paper>
    </>
  );

  const renderNoBusinessesSlide = () => (
    <Box sx={{ pt: 4, pb: 4 }}>
      <h4 style={{ fontSize: '24px', textAlign: 'center', margin: 0 }}>
        We didn't find any businesses linked to you...
      </h4>
      <p style={{ textAlign: 'center', marginTop: 8 }}>
        No worries! Ask your business admin to grant you access, or reach out to us if you think this is a mistake.
      </p>
    </Box>
  );

  const renderFinalSlide = () => (
    <Box sx={{ pt: 4, pb: 4 }}>
      <h4 style={{ fontSize: '24px', textAlign: 'center', margin: 0 }}>
        That's it!
      </h4>
      <p style={{ textAlign: 'center', marginTop: 8 }}>
        You're all set to start exploring the BCK Business Manager. Click “Finish” to dive in and take control of your online presence.
      </p>
    </Box>
  );

  const renderPrimaryUserSlide = () => (
    <>
      <h4 style={{ fontSize: '24px', textAlign: 'center', margin: 0 }}>
        You're the Primary User
      </h4>
      <p style={{ textAlign: 'center', marginTop: 8 }}>
        As the main contact for a business account, you've got a few extra powers to keep things running smoothly.
      </p>
      <Box display="flex" justifyContent="center" mt={2}>
        <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Check sx={{ color: Colors.primaryColor, mr: 1 }} />
            Invite and manage user access
          </li>
          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Check sx={{ color: Colors.primaryColor, mr: 1 }} />
            Update billing and account details
          </li>
          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Check sx={{ color: Colors.primaryColor, mr: 1 }} />
            Act as the go-to contact for support
          </li>
        </Box>
      </Box>
    </>
  );

  const renderStepContent = (step) => {
    if (!steps[step]) return <></>;
    const content = steps[step]();

    return (
      <Fade in={fadeIn} timeout={400} key={`fade-${stepKey}`}>
        <Box>
        <Paper
          elevation={1}
          sx={{
            p: 4,
            mt: 2,
            borderRadius: '12px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
          }}
        >{content}</Paper>
        </Box>
      </Fade>
    );
  };

  const renderCompleteSetupContent = () => (
    <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12}>{renderWelcome()}</Grid>
        <Grid item xs={12} md={7}>
          {renderStepContent(activeStep)}
          <Box sx={{ backgroundColor: 'transparent', pl: 0, pr: 0 }}>
            <MobileStepper
              variant="dots"
              steps={steps.length}
              position="static"
              activeStep={activeStep}
              nextButton={
                <LoadingButton onClick={handleNext} isLoading={isLoading} disabled={isLoading}>
                  {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                </LoadingButton>
              }
              backButton={
                <TextButton onClick={handleBack} disabled={activeStep === 0 || isLoading}>
                  Back
                </TextButton>
              }
              sx={{ mt: 2, backgroundColor: 'transparent', pl: 0, pr: 0 }}
            />
          </Box>
        </Grid>
      </Grid>
  )

  const renderNoLinkedBusinessesContent = () => (
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12}>{renderNoLinkedBusinessNotice()}</Grid>
      <Grid item xs={12} md={9}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 15,
            gap: '24px',
            width: '100%',
            mt: 4,
          }}
        >
          {[
            {
              label: 'Need Access?',
              description: 'Think you should already be connected? Reach out and we’ll help sort it out.',
              icon: <SupportAgentOutlined sx={{ fontSize: 40, color: Colors.primaryColor, mb: 1 }} />,
              onClick: () => window.open('https://consultwithkyle.com/contact-us', '_blank'),
            },
            {
              label: 'Start With BCK',
              description: 'Ready to get going? Let’s help you launch your account with us.',
              icon: <RocketLaunchOutlined sx={{ fontSize: 40, color: Colors.primaryColor, mb: 1 }} />,
              onClick: () => window.open('https://consultwithkyle.com/services/managed-wordpress-hosting/get-started/', '_blank'),
            },
            {
              label: 'What’s BCK?',
              description: 'New around here? See how we can help your business thrive online.',
              icon: <InfoOutlined sx={{ fontSize: 40, color: Colors.primaryColor, mb: 1 }} />,
              onClick: () => window.open('https://consultwithkyle.com/services/', '_blank'),
            },
          ].map(({ label, description, icon, onClick }) => (
            <Box
              key={label}
              onClick={onClick}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '12px',
                padding: '24px',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 6px 14px rgba(0, 0, 0, 0.1)',
                  transform: 'scale(1.03)',
                },
              }}
            >
              {icon}
              <h6 style={{ fontSize: '18px', margin: '8px 0 4px 0' }}>{label}</h6>
              <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>{description}</p>
            </Box>
          ))}
        </Box>

      </Grid>
    </Grid>
  )

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
      }}
    >
      <StyledPage>
        { isSetup ? renderNoLinkedBusinessesContent() : renderCompleteSetupContent()}
      </StyledPage>
    </Box>
  );
};

export default observer(withRouter(UserAccountSetupPage));
